// 该组件是 Popup 和 Dialog 的响应式封装，根据设备类型选择合适的组件
'use client';

import { SxProps, Theme } from '@mui/material';
import { ReactNode, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import Dialog from '../Dialog';
import Popup, { PopupPosition, PopupProps } from '../Popup';
import WapOnly from '../Responsive/WapOnly';
import WebOnly from '../Responsive/WebOnly';

export type { PopupPosition } from '../Popup';

export interface PopupResponsiveProps extends PopupProps {
  /**
   * 弹出层标题
   */
  title?: ReactNode;
  /**
   * 是否显示弹出层
   */
  open?: boolean;
  /**
   * 弹出位置 (仅在移动端生效)
   * @default 'bottom'
   */
  position?: PopupPosition;
  /**
   * 自定义宽度，仅 position 为 left 或 right 时有效 (仅在移动端生效)
   * @default '60%'
   */
  width?: string | number;
  /**
   * 自定义高度，仅 position 为 top 或 bottom 时有效 (仅在移动端生效)
   * @default 'auto'
   */
  height?: string | number;
  /**
   * 是否显示圆角 (仅在移动端生效)
   * @default true
   */
  round?: boolean;
  /**
   * 圆角大小(px) (仅在移动端生效)
   * @default 16
   */
  borderRadius?: number;
  /**
   * 是否显示遮罩层
   * @default true
   */
  overlay?: boolean;
  /**
   * 点击遮罩层是否关闭弹出层
   * @default true
   */
  closeOnClickOverlay?: boolean;
  /**
   * 是否隐藏关闭图标
   * @default false
   */
  hideCloseIcon?: boolean;
  /**
   * 弹出层内容
   */
  children?: ReactNode;
  /**
   * 自定义样式
   */
  style?: React.CSSProperties;
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 自定义body类名
   */
  bodyClassName?: string;
  /**
   * 关闭弹出层时触发
   */
  onClose?: () => void;
  /**
   * 打开弹出层时触发
   */
  onOpen?: () => void;
  /**
   * 禁用自动获取焦点，解决焦点管理问题
   * @default false
   */
  disableAutoFocus?: boolean;
  /**
   * 挂载时是否保留组件，避免内容丢失
   * @default false
   */
  keepMounted?: boolean;
  /**
   * 自定义样式
   */
  sx?: SxProps<Theme>;
  /**
   * 是否显示标题下方的分割线
   * @default true
   */
  showDivider?: boolean;
  /**
   * 容器元素，用于控制挂载位置
   * 如果为 null，则挂载到 body
   * 如果为 HTMLElement，则挂载到指定元素
   */
  container?: HTMLElement | null;
  /**
   * 自定义 z-index 值
   */
  zIndex?: number;

  /**
   * 底部操作区内容
   */
  footer?: ReactNode;
  /**
   * 是否显示底部区域
   * @default false
   */
  hasFooter?: boolean;
  /**
   * 确认按钮文本
   * @default 'Ok'
   */
  okText?: string;
  /**
   * 取消按钮文本
   * @default 'Cancel'
   */
  cancelText?: string;
  /**
   * 确认按钮点击回调
   */
  onOk?: () => void;
  /**
   * 取消按钮点击回调
   */
  onCancel?: () => void;
  /**
   * 是否显示加载状态
   * @default false
   */
  loading?: boolean;
}

export interface PopupResponsiveRef {
  open: () => void;
  close: () => void;
}

const PopupResponsive = forwardRef<PopupResponsiveRef, PopupResponsiveProps>((props, ref) => {
  const {
    open = false,
    onOpen,
    onClose,
    // 其他属性不需要在这里解构，直接传递给子组件
  } = props;

  const [visible, setVisible] = useState(open);

  // 同步外部open状态
  useEffect(() => {
    setVisible(open);
  }, [open]);

  useImperativeHandle(ref, () => ({
    open: () => {
      setVisible(true);
      onOpen?.();
    },
    close: () => {
      setVisible(false);
      onClose?.();
    },
  }));

  // 处理关闭事件
  const handleClose = () => {
    setVisible(false);
    onClose?.();
  };

  // 将 Popup 的属性映射到 Dialog 的属性
  const mapToDialogProps = () => {
    const {
      hideCloseIcon,
      showDivider,
      footer,
      hasFooter,
      okText,
      cancelText,
      onOk,
      onCancel,
      loading,
      // 这些属性在 Popup 中使用，但在 Dialog 中不需要或有不同名称
      position,
      width,
      height,
      round,
      borderRadius,
      overlay,
      closeOnClickOverlay,
      bodyClassName,
      ...otherProps
    } = props;

    const dialogProps = {
      ...otherProps,
      open: visible,
      onClose: handleClose,
      showCloseIcon: !hideCloseIcon,
      showTitleDivider: showDivider,
      okText,
      cancelText,
      onOk,
      onCancel,
      loading,
      closeOnClickOverlay,
      // Dialog 没有 bodyClassName，但有 contentProps
      contentProps: {
        className: bodyClassName,
      },
      // 如果有自定义 footer，包含它
      ...(footer !== undefined ? { footer } : {}),
    };

    return dialogProps;
  };

  // 根据按钮配置决定使用哪种 Dialog 组件
  const getDialogComponent = () => {
    const { footer, onOk, onCancel } = props;
    const dialogProps = mapToDialogProps();

    // 如果有自定义 footer，使用基础 Dialog
    if (footer !== undefined) {
      return <Dialog {...dialogProps} />;
    }

    // 如果只有确认按钮，没有取消按钮，使用 Dialog.Confirm
    if (onOk && !onCancel) {
      return <Dialog.Confirm {...dialogProps} />;
    }

    // 如果只有关闭按钮，没有底部按钮，使用 Dialog.Info
    if (!onOk && !onCancel) {
      if (dialogProps.showTitleDivider === undefined) {
        dialogProps.showTitleDivider = true;
      }
      return <Dialog.Info {...dialogProps} />;
    }

    // 默认使用基础 Dialog（有确认和取消两个按钮）
    return <Dialog {...dialogProps} />;
  };

  return (
    <>
      <WapOnly>
        <Popup {...props} open={visible} onClose={handleClose} />
      </WapOnly>
      <WebOnly>{getDialogComponent()}</WebOnly>
    </>
  );
});

PopupResponsive.displayName = 'PopupResponsive';

export default PopupResponsive;
